"use strict";
/**
 * Note that some limitations exist in the type system pertaining to
 * polymorphic (generic) functions which could impact the usage of any of the
 * functions here. All of these functions will work provided monomorphic
 * (non-generic) input functions.
 *
 * @since 0.1.0
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.fork = exports.uncurry5 = exports.uncurry4 = exports.uncurry3 = exports.uncurry2 = exports.curry5 = exports.curry5T = exports.curry4 = exports.curry4T = exports.curry3 = exports.curry3T = exports.curry2 = exports.curry2T = exports.memoize = exports.construct = exports.until = exports.when = exports.unless = exports.ifElse = exports.guard = exports.unary = exports.withIndex = exports.flip = void 0;
const O = require("fp-ts/Option");
const M = require("fp-ts/Map");
const A = require("fp-ts/Array");
const function_1 = require("fp-ts/function");
const Predicate_1 = require("fp-ts/Predicate");
const Monoid_1 = require("fp-ts/Monoid");
const Semigroup_1 = require("fp-ts/Semigroup");
/**
 * Flip the function/argument order of a curried function.
 *
 * @example
 * import { flip } from 'fp-ts-std/Function';
 *
 * const prepend = (x: string) => (y: string): string => x + y;
 * const append = flip(prepend);
 *
 * assert.strictEqual(prepend('x')('y'), 'xy');
 * assert.strictEqual(append('x')('y'), 'yx');
 *
 * @since 0.1.0
 */
const flip = (f) => (...b) => (...a) => f(...a)(...b);
exports.flip = flip;
/**
 * Given a curried function with an iterative callback, this returns a new
 * function that behaves identically except that it also supplies an index for
 * each iteration of the callback.
 *
 * @example
 * import * as A from 'fp-ts/Array';
 * import { withIndex } from 'fp-ts-std/Function';
 *
 * const mapWithIndex = withIndex<number, number, number>(A.map);
 * assert.deepStrictEqual(mapWithIndex(i => x => x + i)([1, 2, 3]), [1, 3, 5]);
 *
 * @since 0.5.0
 */
const withIndex = f => g => xs => {
    let i = 0; // eslint-disable-line functional/no-let
    return f(y => g(i++)(y))(xs);
};
exports.withIndex = withIndex;
/**
 * Converts a variadic function to a unary function.
 *
 * Whilst this isn't very useful for functions that ought to be curried,
 * it is helpful for functions which take an indefinite number of arguments
 * instead of more appropriately an array.
 *
 * @example
 * import { unary } from 'fp-ts-std/Function';
 *
 * const max = unary(Math.max);
 *
 * assert.strictEqual(max([1, 3, 2]), 3);
 *
 * @since 0.6.0
 */
const unary = (f) => (xs) => f(...xs);
exports.unary = unary;
/**
 * Given an array of predicates and morphisms, returns the first morphism output
 * for which the paired predicate succeeded. If all predicates fail, the
 * fallback value is returned.
 *
 * This is analagous to Haskell's guards.
 *
 * @example
 * import { guard } from 'fp-ts-std/Function';
 * import { constant } from 'fp-ts/function';
 *
 * const numSize = guard<number, string>([
 *     [n => n > 100, n => `${n} is large!`],
 *     [n => n > 50, n => `${n} is medium.`],
 *     [n => n > 0, n => `${n} is small...`],
 * ])(n => `${n} is not a positive number.`);
 *
 * assert.strictEqual(numSize(101), '101 is large!');
 * assert.strictEqual(numSize(99), '99 is medium.');
 * assert.strictEqual(numSize(5), '5 is small...');
 * assert.strictEqual(numSize(-3), '-3 is not a positive number.');
 *
 * @since 0.6.0
 */
const guard = (branches) => (fallback) => (input) => function_1.pipe(branches, A.map(([f, g]) => function_1.flow(O.fromPredicate(f), O.map(g))), Monoid_1.concatAll(function_1.getMonoid(O.getMonoid(Semigroup_1.first()))()), function_1.apply(input), O.getOrElse(() => fallback(input)));
exports.guard = guard;
/**
 * Creates a function that processes the first morphism if the predicate
 * succeeds, else the second morphism.
 *
 * @example
 * import { ifElse } from 'fp-ts-std/Function';
 * import { increment, decrement } from 'fp-ts-std/Number';
 * import { Predicate } from 'fp-ts/Predicate';
 *
 * const isPositive: Predicate<number> = n => n > 0;
 * const normalise = ifElse(decrement)(increment)(isPositive);
 *
 * assert.strictEqual(normalise(-3), -2);
 * assert.strictEqual(normalise(3), 2);
 *
 * @since 0.6.0
 */
const ifElse = (onTrue) => (onFalse) => (f) => (x) => f(x) ? onTrue(x) : onFalse(x);
exports.ifElse = ifElse;
/**
 * Runs the provided morphism on the input value if the predicate fails.
 *
 * @example
 * import { unless } from 'fp-ts-std/Function';
 * import { increment } from 'fp-ts-std/Number';
 * import { Predicate } from 'fp-ts/Predicate';
 *
 * const isEven: Predicate<number> = n => n % 2 === 0;
 * const ensureEven = unless(isEven)(increment);
 *
 * assert.strictEqual(ensureEven(1), 2);
 * assert.strictEqual(ensureEven(2), 2);
 *
 * @since 0.6.0
 */
const unless = (f) => (onFalse) => x => f(x) ? x : onFalse(x);
exports.unless = unless;
/**
 * Runs the provided morphism on the input value if the predicate holds.
 *
 * @example
 * import { when } from 'fp-ts-std/Function';
 * import { increment } from 'fp-ts-std/Number';
 * import { Predicate } from 'fp-ts/Predicate';
 *
 * const isEven: Predicate<number> = n => n % 2 === 0;
 * const ensureOdd = when(isEven)(increment);
 *
 * assert.strictEqual(ensureOdd(1), 1);
 * assert.strictEqual(ensureOdd(2), 3);
 *
 * @since 0.6.0
 */
exports.when = function_1.flow(Predicate_1.not, exports.unless);
/**
 * Yields the result of applying the morphism to the input until the predicate
 * holds.
 *
 * @example
 * import { until } from 'fp-ts-std/Function';
 * import { increment } from 'fp-ts-std/Number';
 * import { Predicate } from 'fp-ts/Predicate';
 *
 * const isOver100: Predicate<number> = n => n > 100;
 * const doubleUntilOver100 = until(isOver100)(n => n * 2);
 *
 * assert.strictEqual(doubleUntilOver100(1), 128);
 *
 * @since 0.6.0
 */
const until = (f) => (g) => {
    const h = x => (f(x) ? x : h(g(x)));
    return h;
};
exports.until = until;
/**
 * Wraps a constructor function for functional invocation.
 *
 * @example
 * import { construct } from 'fp-ts-std/Function';
 *
 * const mkURL = construct(URL);
 *
 * const xs: [string, string] = ['/x/y/z.html', 'https://samhh.com'];
 *
 * assert.deepStrictEqual(mkURL(xs), new URL(...xs));
 *
 * @since 0.7.0
 */
const construct = (x) => (xs) => new x(...xs);
exports.construct = construct;
/**
 * Given a function and an `Eq` instance for determining input equivalence,
 * returns a new function that caches the result of applying an input to said
 * function. If the cache hits, the cached value is returned and the function
 * is not called again. Useful for expensive computations.
 *
 * Provided the input function is pure, this function is too.
 *
 * The cache is implemented as a simple `Map`. There is no mechanism by which
 * cache entries can be cleared from memory.
 *
 * @example
 * import { memoize } from 'fp-ts-std/Function';
 * import { add } from 'fp-ts-std/Number';
 * import { eqNumber } from 'fp-ts/Eq';
 *
 * let runs = 0;
 * const f = memoize(eqNumber)<number>(n => {
 *     runs++;
 *     return add(5)(n);
 * });
 *
 * assert.strictEqual(runs, 0);
 * assert.strictEqual(f(2), 7);
 * assert.strictEqual(runs, 1);
 * assert.strictEqual(f(2), 7);
 * assert.strictEqual(runs, 1);
 *
 * @since 0.7.0
 */
const memoize = (eq) => (f) => {
    const cache = new Map();
    return k => {
        const cached = M.lookup(eq)(k)(cache);
        if (O.isSome(cached))
            return cached.value; // eslint-disable-line functional/no-conditional-statement
        const val = f(k);
        cache.set(k, val); // eslint-disable-line functional/no-expression-statement
        return val;
    };
};
exports.memoize = memoize;
/**
 * Curry a function with binary tuple input.
 *
 * @example
 * import { curry2T } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat2 = ([a, b]: [string, string]): string =>
 *      a + b;
 * assert.strictEqual(curry2T(concat2)('a')('b'), concat2(['a', 'b']));
 *
 * @since 0.7.0
 */
const curry2T = (f) => (a) => (b) => f([a, b]);
exports.curry2T = curry2T;
/**
 * Curry a function with binary input.
 *
 * @example
 * import { curry2 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat2 = (a: string, b: string): string =>
 *      a + b;
 * assert.strictEqual(curry2(concat2)('a')('b'), concat2('a', 'b'));
 *
 * @since 0.7.0
 */
exports.curry2 = function_1.flow(exports.unary, exports.curry2T);
/**
 * Curry a function with ternary tuple input.
 *
 * @example
 * import { curry3T } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat3 = ([a, b, c]: [string, string, string]): string =>
 *      a + b + c;
 * assert.strictEqual(curry3T(concat3)('a')('b')('c'), concat3(['a', 'b', 'c']));
 *
 * @since 0.7.0
 */
const curry3T = (f) => (a) => (b) => (c) => f([a, b, c]);
exports.curry3T = curry3T;
/**
 * Curry a function with ternary input.
 *
 * @example
 * import { curry3 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat3 = (a: string, b: string, c: string): string =>
 *      a + b + c;
 * assert.strictEqual(curry3(concat3)('a')('b')('c'), concat3('a', 'b', 'c'));
 *
 * @since 0.7.0
 */
exports.curry3 = function_1.flow(exports.unary, exports.curry3T);
/**
 * Curry a function with quaternary tuple input.
 *
 * @example
 * import { curry4T } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat4 = ([a, b, c, d]: [string, string, string, string]): string =>
 *      a + b + c + d;
 * assert.strictEqual(curry4T(concat4)('a')('b')('c')('d'), concat4(['a', 'b', 'c', 'd']));
 *
 * @since 0.7.0
 */
const curry4T = (f) => (a) => (b) => (c) => (d) => f([a, b, c, d]);
exports.curry4T = curry4T;
/**
 * Curry a function with quaternary input.
 *
 * @example
 * import { curry4 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat4 = (a: string, b: string, c: string, d: string): string =>
 *      a + b + c + d;
 * assert.strictEqual(curry4(concat4)('a')('b')('c')('d'), concat4('a', 'b', 'c', 'd'));
 *
 * @since 0.7.0
 */
exports.curry4 = function_1.flow(exports.unary, exports.curry4T);
/**
 * Curry a function with quinary tuple input.
 *
 * @example
 * import { curry5T } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat5 = ([a, b, c, d, e]: [string, string, string, string, string]): string =>
 *      a + b + c + d + e;
 * assert.strictEqual(curry5T(concat5)('a')('b')('c')('d')('e'), concat5(['a', 'b', 'c', 'd', 'e']));
 *
 * @since 0.7.0
 */
const curry5T = (f) => (a) => (b) => (c) => (d) => (e) => f([a, b, c, d, e]);
exports.curry5T = curry5T;
/**
 * Curry a function with quinary input.
 *
 * @example
 * import { curry5 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat5 = (a: string, b: string, c: string, d: string, e: string): string =>
 *      a + b + c + d + e;
 * assert.strictEqual(curry5(concat5)('a')('b')('c')('d')('e'), concat5('a', 'b', 'c', 'd', 'e'));
 *
 * @since 0.7.0
 */
exports.curry5 = function_1.flow(exports.unary, exports.curry5T);
/**
 * Uncurry a binary function.
 *
 * @example
 * import { uncurry2 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat2 = (a: string): Endomorphism<string> => b =>
 *      a + b;
 * assert.strictEqual(uncurry2(concat2)(['a', 'b']), concat2('a')('b'));
 *
 * @since 0.7.0
 */
const uncurry2 = (f) => ([a, b]) => f(a)(b);
exports.uncurry2 = uncurry2;
/**
 * Uncurry a ternary function.
 *
 * @example
 * import { uncurry3 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat3 = (a: string) => (b: string): Endomorphism<string> => c =>
 *      a + b + c;
 * assert.strictEqual(uncurry3(concat3)(['a', 'b', 'c']), concat3('a')('b')('c'));
 *
 * @since 0.7.0
 */
const uncurry3 = (f) => ([a, b, c]) => f(a)(b)(c);
exports.uncurry3 = uncurry3;
/**
 * Uncurry a quaternary function.
 *
 * @example
 * import { uncurry4 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat4 = (a: string) => (b: string) => (c: string): Endomorphism<string> => d =>
 *      a + b + c + d;
 * assert.strictEqual(uncurry4(concat4)(['a', 'b', 'c', 'd']), concat4('a')('b')('c')('d'));
 *
 * @since 0.7.0
 */
const uncurry4 = (f) => ([a, b, c, d]) => f(a)(b)(c)(d);
exports.uncurry4 = uncurry4;
/**
 * Uncurry a quinary function.
 *
 * @example
 * import { uncurry5 } from 'fp-ts-std/Function';
 * import { Endomorphism } from 'fp-ts/Endomorphism';
 *
 * const concat5 = (a: string) => (b: string) => (c: string) => (d: string): Endomorphism<string> => e =>
 *      a + b + c + d + e;
 * assert.strictEqual(uncurry5(concat5)(['a', 'b', 'c', 'd', 'e']), concat5('a')('b')('c')('d')('e'));
 *
 * @since 0.7.0
 */
const uncurry5 = (f) => ([a, b, c, d, e]) => f(a)(b)(c)(d)(e);
exports.uncurry5 = uncurry5;
function fork(fs) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return (x) => fs.map(f => f(x));
}
exports.fork = fork;
